"use client";

import {
	BUYER_ACTIONS_MUTATION,
	BUYER_DASHBOARD_QUERY,
	BUYER_DASHBOARD_SUGGESTION_LIST_QUERY,
} from "@/gql/buyer-dashboard";
import { BUYER_REJECT_PROPERTY_MUTATION } from "@/gql/buyer-reject";
import { REJECT_PREFERENCE_SUGGESTION_MUTATION } from "@/gql/preference";
import { GET_SHORTLISTED_PROJECTS_QUERY, PROJECT_REQUEST_VISIT, PROJECT_SHORTLIST_MUTATION } from "@/gql/project";
import {
	UNIT_REMOVE_SHORTLIST_MUTATION,
	UNIT_REQUEST_VISIT,
	UNIT_SHORTLIST_MUTATION,
	UNIT_SUGGESTION_SHORTLIST_MUTATION,
} from "@/gql/unit";
import { BuyerProject } from "@/types/buyerProject";
import { BuyerSuggestion } from "@/types/buyerSuggestion";
import { BuyerUnit } from "@/types/buyerUnit";
import { PropertyType } from "@/types/property";
import { trackEvent } from "@/utils/analytics/track-event";
import { trackRequestVisitForProject, trackRequestVisitForUnit } from "@/utils/analytics/track-request-visit";
import { trackShortlistProject, trackShortlistUnit } from "@/utils/analytics/track-shortlist";
import { logger } from "@/utils/logger";
import { useMutation } from "@apollo/client";
import { toast } from "sonner";

import { useBuyerPropertiesStore } from "@/stores/buyer/buyer-properties-store";
import { utmUtils } from "@/utils/utm";
import { useAuth } from "./use-auth";

/**
 * Source context for tracking where buyer actions are triggered from
 */
export type BuyerActionSource =
	| "dashboard"
	| "search"
	| "property_detail"
	| "suggestion_card"
	| "shortlist_page"
	| "map_view"
	| "preference_list"
	| "customer_care_widget"
	| "unknown"
	| "project_title";

export function useBuyerActions() {
	const { user } = useAuth();
	const optimisticAddToShortlist = useBuyerPropertiesStore(state => state.optimisticAddToShortlist);
	const optimisticRemoveFromShortlist = useBuyerPropertiesStore(state => state.optimisticRemoveFromShortlist);

	/**
	 * Dynamically computes buyer group ID to avoid stale closure issues.
	 *
	 * Why dynamic computation instead of closure?
	 * - Previously used: `const buyerGroupId = user?.groups?.find(...)?.id`
	 * - Problem: When hook initializes before auth completes, `user` is null
	 * - Closure captures this undefined value and never updates
	 * - Result: All mutations fail with "buyer group ID is missing" even after login
	 *
	 * Solution: Compute dynamically on each call to always get current user state
	 */
	const getBuyerGroupId = () =>
		user?.groups?.find((group: { relationship?: string }) => group.relationship === "buyer")?.id;

	/**
	 * Validates that buyer group ID exists and shows appropriate error if missing.
	 * Returns the buyer group ID if valid, or undefined if missing.
	 * Handles race conditions during authentication hydration.
	 */
	const validateBuyerGroupId = ({
		method,
		mutation,
		propertyId,
		additionalContext,
	}: {
		method: string;
		mutation: string;
		propertyId?: string;
		additionalContext?: Record<string, string | number | boolean | undefined>;
	}): string | undefined => {
		const currentBuyerGroupId = getBuyerGroupId();
		if (!currentBuyerGroupId) {
			logger.warn(`Cannot perform ${method} - buyer group ID is missing`, {
				method,
				mutation,
				propertyId,
				...additionalContext,
			});
			toast.error("Authentication in progress. Please try again in a moment.", {
				description: "If this persists, please login again.",
			});
			return undefined;
		}
		return currentBuyerGroupId;
	};

	const [shortlistUnit, { loading: shortlistLoading }] = useMutation(UNIT_SHORTLIST_MUTATION, {
		refetchQueries: [
			{
				query: BUYER_DASHBOARD_QUERY,
				variables: {
					params: {
						groupOne: { id: getBuyerGroupId() },
					},
				},
			},
		],
	});

	const [shortlistUnitSuggestion] = useMutation(UNIT_SUGGESTION_SHORTLIST_MUTATION, {
		refetchQueries: [
			{
				query: BUYER_DASHBOARD_QUERY,
				variables: {
					params: {
						groupOne: { id: getBuyerGroupId() },
					},
				},
			},
		],
	});

	const [rejectPropertySuggestion] = useMutation(REJECT_PREFERENCE_SUGGESTION_MUTATION, {
		refetchQueries: [
			{
				query: BUYER_DASHBOARD_SUGGESTION_LIST_QUERY,
				variables: {
					params: {
						getSuggestedProperties: { buyerId: getBuyerGroupId(), limit: 100, skip: 0, status: "pending" },
					},
				},
			},
		],
	});

	const [removeShortlistUnit, { loading: removeShortlistLoading }] = useMutation(UNIT_REMOVE_SHORTLIST_MUTATION, {
		refetchQueries: [
			{
				query: BUYER_DASHBOARD_QUERY,
				variables: {
					params: {
						groupOne: { id: getBuyerGroupId() },
					},
				},
			},
		],
	});

	const [shortlistProject] = useMutation(PROJECT_SHORTLIST_MUTATION, {
		awaitRefetchQueries: true, // Wait for refetch queries to complete before resolving mutation
		refetchQueries: () => {
			const currentBuyerGroupId = getBuyerGroupId();
			const currentUserId = user?.id;

			logger.info("🔄 [useBuyerActions] Preparing refetch queries with:", {
				buyerGroupId: currentBuyerGroupId,
				userId: currentUserId,
			});

			return [
				{
					query: BUYER_DASHBOARD_QUERY,
					variables: {
						params: {
							groupOne: { id: currentBuyerGroupId },
						},
					},
				},
				{
					query: GET_SHORTLISTED_PROJECTS_QUERY,
					variables: {
						filters: {
							getShortlistedProjects: {
								buyerId: currentBuyerGroupId || "",
								userId: currentUserId || "",
							},
						},
					},
				},
			];
		},
	});

	const [rejectProperty] = useMutation(BUYER_REJECT_PROPERTY_MUTATION, {
		refetchQueries: [
			{
				query: BUYER_DASHBOARD_QUERY,
				variables: {
					params: {
						groupOne: { id: getBuyerGroupId() },
					},
				},
			},
			{
				query: GET_SHORTLISTED_PROJECTS_QUERY,
				variables: {
					filters: {
						getShortlistedProjects: {
							buyerId: getBuyerGroupId() || "",
							userId: user?.id || "",
						},
					},
				},
			},
		],
	});

	const [requestUnitVisit] = useMutation(UNIT_REQUEST_VISIT, {
		refetchQueries: [
			{
				query: BUYER_DASHBOARD_QUERY,
				variables: {
					params: {
						groupOne: { id: getBuyerGroupId() },
					},
				},
			},
		],
	});

	const [requestProjectVisit] = useMutation(PROJECT_REQUEST_VISIT, {
		refetchQueries: [
			{
				query: BUYER_DASHBOARD_QUERY,
				variables: {
					params: {
						groupOne: { id: getBuyerGroupId() },
					},
				},
			},
		],
	});

	const [submitBuyerAction] = useMutation(BUYER_ACTIONS_MUTATION, {
		refetchQueries: [
			{
				query: BUYER_DASHBOARD_QUERY,
				variables: {
					params: {
						groupOne: { id: getBuyerGroupId() },
					},
				},
			},
		],
	});

	const handleShortlist = async (
		property: BuyerUnit | BuyerProject | BuyerSuggestion,
		source: BuyerActionSource = "unknown",
	) => {
		try {
			const type = property.__typename;

			if (type === "SuggestedPropertiesInfo") {
				const suggestion = property as BuyerSuggestion;
				const propertyId = suggestion.type === "unit" ? suggestion.unitId : suggestion.projectId;
				const propertyType = suggestion.type === "unit" ? PropertyType.UNIT : PropertyType.PROJECT;

				if (!propertyId) return;

				// Validate buyer group ID (handles race conditions)
				const currentBuyerGroupId = validateBuyerGroupId({
					method: "handleShortlist",
					mutation: "UNIT_SUGGESTION_SHORTLIST_MUTATION",
					propertyId,
					additionalContext: { suggestionType: suggestion.type },
				});
				if (!currentBuyerGroupId) return;

				// Optimistic update - immediately update UI
				optimisticAddToShortlist(propertyId, propertyType);

				try {
					if (suggestion.type === "unit") {
						await shortlistUnitSuggestion({
							variables: {
								filters: {
									shortlistUnit: {
										unitId: suggestion.unitId,
										buyerId: currentBuyerGroupId,
										shortlist: true,
										isMobile: false,
										info: {
											utm: utmUtils.getCurrent(),
										},
									},
								},
							},
						});
						// Track unit shortlist
						trackShortlistUnit({ unitId: suggestion.unitId!, action: "add" });
					} else if (suggestion.type === "sp") {
						await shortlistProject({
							variables: {
								params: {
									shortlistProject: {
										shortlist: true,
										projectId: suggestion.projectId,
										buyerId: currentBuyerGroupId,
										isMobile: false,
									},
								},
							},
						});
						// Track project shortlist
						trackShortlistProject({ projectId: suggestion.projectId!, action: "add" });
					}

					// Track shortlist action with source context
					trackEvent({
						google: {
							event: propertyType === PropertyType.UNIT ? "shortlist_unit" : "shortlist_project",
							options: {
								[propertyType === PropertyType.UNIT ? "unit_id" : "project_id"]: propertyId,
								source,
								action_type: "suggestion",
							},
						},
						facebook: {
							event: "AddToWishlist",
							options: {
								content_id: propertyId,
								content_type: propertyType === PropertyType.UNIT ? "unit" : "project",
								source,
							},
						},
					});

					toast.success("Property added to shortlist");
				} catch (error) {
					// Revert optimistic update on error
					optimisticRemoveFromShortlist(
						propertyId,
						suggestion.type === "unit" ? PropertyType.UNIT : PropertyType.PROJECT,
					);
					logger.error("Failed to shortlist suggestion", {
						method: "handleShortlist",
						mutation: "UNIT_SUGGESTION_SHORTLIST_MUTATION",
						propertyId,
						error,
					});
					toast.error("Failed to add property to shortlist");
					throw error;
				}
			} else if (type === "BuyerUnit") {
				const buyerUnit = property as BuyerUnit;
				const propertyId = buyerUnit.unit?.id;

				if (!propertyId) return;

				// Ensure buyer group ID exists before making the mutation
				const currentBuyerGroupId = getBuyerGroupId();
				if (!currentBuyerGroupId) {
					logger.warn("Cannot shortlist unit - buyer group ID is missing", {
						method: "handleShortlist",
						mutation: "UNIT_SHORTLIST_MUTATION",
						propertyId,
						buyerGroupId: currentBuyerGroupId,
					});
					toast.error("Please login to shortlist this property");
					return;
				}

				// Optimistic update - immediately update UI
				optimisticAddToShortlist(propertyId, PropertyType.UNIT);

				try {
					await shortlistUnit({
						variables: {
							filters: {
								shortlist: {
									unitId: propertyId,
									buyerId: currentBuyerGroupId,
									info: {
										utm: utmUtils.getCurrent(),
									},
								},
							},
						},
					});

					// Track shortlist action with source context
					trackEvent({
						google: {
							event: "shortlist_unit",
							options: {
								unit_id: propertyId,
								source,
								action_type: "direct",
							},
						},
						facebook: {
							event: "AddToWishlist",
							options: {
								content_id: propertyId,
								content_type: "unit",
								source,
							},
						},
					});

					toast.success("Property added to shortlist");
				} catch (error) {
					// Revert optimistic update on error
					optimisticRemoveFromShortlist(propertyId, PropertyType.UNIT);
					logger.error("Failed to shortlist unit", {
						method: "handleShortlist",
						mutation: "UNIT_SHORTLIST_MUTATION",
						propertyId,
						error,
					});
					toast.error("Failed to add property to shortlist");
					throw error;
				}
			} else if (type === "BuyerProject") {
				const bp = property as BuyerProject;
				const propertyId = bp.project?.id;

				if (!propertyId) return;

				// Validate buyer group ID (handles race conditions)
				const currentBuyerGroupId = validateBuyerGroupId({
					method: "handleShortlist",
					mutation: "PROJECT_SHORTLIST_MUTATION",
					propertyId,
				});
				if (!currentBuyerGroupId) return;

				// Optimistic update - immediately update UI
				optimisticAddToShortlist(propertyId, PropertyType.PROJECT);

				try {
					const result = await shortlistProject({
						variables: {
							params: {
								shortlistProject: {
									shortlist: true,
									projectId: propertyId,
									buyerId: currentBuyerGroupId,
									isMobile: false,
								},
							},
						},
					});

					logger.info("✅ [useBuyerActions] Project shortlist mutation completed:", {
						projectId: propertyId,
						buyerGroupId: currentBuyerGroupId,
						mutationResult: result.data,
						mutationVariables: {
							shortlist: true,
							projectId: propertyId,
							buyerId: currentBuyerGroupId,
							isMobile: false,
						},
						hasErrors: !!result.errors,
						errors: result.errors,
					});

					// Track shortlist action with source context
					trackEvent({
						google: {
							event: "shortlist_project",
							options: {
								project_id: propertyId,
								source,
								action_type: "direct",
							},
						},
						facebook: {
							event: "AddToWishlist",
							options: {
								content_id: propertyId,
								content_type: "project",
								source,
							},
						},
					});

					toast.success("Project added to shortlist");
				} catch (error) {
					// Revert optimistic update on error
					optimisticRemoveFromShortlist(propertyId, PropertyType.PROJECT);
					logger.error("❌ [useBuyerActions] Failed to shortlist project:", {
						method: "handleShortlist",
						mutation: "PROJECT_SHORTLIST_MUTATION",
						propertyId,
						buyerGroupId: currentBuyerGroupId,
						error,
						errorMessage: error instanceof Error ? error.message : String(error),
						errorStack: error instanceof Error ? error.stack : undefined,
					});
					toast.error("Failed to add project to shortlist");
					throw error;
				}
			}
		} catch (_error) {
			logger.error("Error adding property to shortlist:", _error);
			toast.error("Failed to add to shortlist");
		}
	};

	const handleRemoveShortlist = async ({
		property,
		source = "unknown",
	}: {
		property: BuyerUnit | BuyerProject;
		source?: BuyerActionSource;
	}) => {
		try {
			const isUnit = "unit" in property && property.unit != null;
			const propertyId = isUnit ? property.unit?.id : (property as BuyerProject).project?.id;
			const propertyType = isUnit ? PropertyType.UNIT : PropertyType.PROJECT;

			if (!propertyId) return;

			// Ensure buyer group ID exists before making the mutation
			const currentBuyerGroupId = getBuyerGroupId();
			if (!currentBuyerGroupId) {
				logger.warn("Cannot remove shortlist - buyer group ID is missing", {
					method: "handleRemoveShortlist",
					mutation: isUnit ? "UNIT_REMOVE_SHORTLIST_MUTATION" : "PROJECT_REMOVE_SHORTLIST_MUTATION",
					propertyId,
					isUnit,
				});
				toast.error("Please login to remove shortlist");
				return;
			}

			// Optimistic update - immediately update UI
			optimisticRemoveFromShortlist(propertyId, propertyType);

			try {
				if (isUnit) {
					await removeShortlistUnit({
						variables: {
							filters: {
								removeShortlist: {
									unitId: propertyId,
									buyerId: currentBuyerGroupId,
								},
							},
						},
					});

					// Track remove shortlist action with source context
					trackEvent({
						google: {
							event: "remove_shortlist_unit",
							options: {
								unit_id: propertyId,
								source,
							},
						},
						facebook: {
							event: "RemoveFromWishlist",
							options: {
								content_id: propertyId,
								content_type: "unit",
								source,
							},
						},
					});

					toast.success("Property removed from shortlist");
				} else {
					// Remove project from shortlist using PROJECT_SHORTLIST_MUTATION with shortlist: false
					logger.info("🗑️ [useBuyerActions] Removing project from shortlist:", {
						projectId: propertyId,
						buyerGroupId: currentBuyerGroupId,
						mutationVariables: {
							shortlist: false,
							projectId: propertyId,
							buyerId: currentBuyerGroupId,
							isMobile: false,
						},
					});

					const result = await shortlistProject({
						variables: {
							params: {
								shortlistProject: {
									shortlist: false, // false = remove from shortlist
									projectId: propertyId,
									buyerId: currentBuyerGroupId,
									isMobile: false,
								},
							},
						},
					});

					logger.info("✅ [useBuyerActions] Project remove from shortlist mutation completed:", {
						projectId: propertyId,
						buyerGroupId: currentBuyerGroupId,
						mutationResult: result.data,
						hasErrors: !!result.errors,
						errors: result.errors,
					});

					logger.info("⏳ [useBuyerActions] Mutation complete, refetch queries should have executed now...");
					logger.info(
						"🔍 [useBuyerActions] Check if GET_SHORTLISTED_PROJECTS_QUERY and BUYER_DASHBOARD_QUERY logs appear above",
					);

					// Track project shortlist removal
					trackShortlistProject({ projectId: propertyId, action: "remove" });

					// Track remove shortlist action with source context
					trackEvent({
						google: {
							event: "remove_shortlist_project",
							options: {
								project_id: propertyId,
								source,
							},
						},
						facebook: {
							event: "RemoveFromWishlist",
							options: {
								content_id: propertyId,
								content_type: "project",
								source,
							},
						},
					});

					toast.success("Project removed from shortlist");
				}
			} catch (error) {
				// Revert optimistic update on error
				optimisticAddToShortlist(propertyId, isUnit ? PropertyType.UNIT : PropertyType.PROJECT);
				logger.error("Failed to remove from shortlist", {
					method: "handleRemoveShortlist",
					mutation: isUnit ? "UNIT_REMOVE_SHORTLIST_MUTATION" : "PROJECT_REMOVE_SHORTLIST_MUTATION",
					propertyId,
					isUnit,
					error,
				});
				toast.error("Failed to remove from shortlist");
				throw error;
			}
		} catch (_error) {
			toast.error("Failed to remove from shortlist");
		}
	};

	const handleScheduleVisit = async (
		property: BuyerUnit | BuyerProject,
		slots: { start: string; end: string }[],
		source: BuyerActionSource = "unknown",
	) => {
		try {
			const isUnit = "unit" in property && property.unit != null;
			const propertyId = isUnit ? property.unit?.id : (property as BuyerProject).project?.id;

			if (!propertyId) return;

			try {
				if (isUnit) {
					// Track unit visit request with source
					trackRequestVisitForUnit(propertyId, { source });

					const result = await requestUnitVisit({
						variables: {
							filters: {
								visitRequest: {
									unitId: propertyId,
									visitRequestDate: slots[0]?.start ? new Date(slots[0].start) : new Date(),
									visitRequestSlots: slots.map(slot => ({
										startTime: new Date(slot.start),
										endTime: new Date(slot.end),
									})),
									info: {
										utm: utmUtils.getCurrent(),
									},
								},
							},
						},
					});

					if (!result.data?.unitMethods?.visitRequest?.visitRequested) {
						logger.error("Failed to request unit visit", {
							method: "handleScheduleVisit",
							mutation: "UNIT_REQUEST_VISIT",
							propertyId,
							result,
						});
						toast.error("Failed to request visit");
						return;
					}

					toast.success("Visit request sent successfully");
				} else {
					// Track project visit request with source
					trackRequestVisitForProject(propertyId, { source });
					const result = await requestProjectVisit({
						variables: {
							params: {
								projectVisitRequest: {
									projectId: propertyId,
									visitRequestDate: slots[0]?.start ? new Date(slots[0].start) : new Date(),
									visitRequestSlots: slots.map(slot => ({
										startTime: new Date(slot.start),
										endTime: new Date(slot.end),
									})),
								},
							},
						},
					});

					if (!result.data?.project?.projectVisitRequest) {
						logger.error("Failed to request project visit", {
							method: "handleScheduleVisit",
							mutation: "PROJECT_REQUEST_VISIT",
							propertyId,
							result,
						});
						toast.error("Failed to request project visit");
						return;
					}

					toast.success("Project visit request sent successfully");
				}
			} catch (error) {
				logger.error("Failed to schedule visit", {
					method: "handleScheduleVisit",
					mutation: isUnit ? "UNIT_REQUEST_VISIT" : "PROJECT_REQUEST_VISIT",
					propertyId,
					isUnit,
					error,
				});
				toast.error("Failed to request visit");
				throw error;
			}
		} catch (error) {
			logger.error("Error requesting visit:", error);
			toast.error("Failed to request visit");
		}
	};

	const handleReschedule = async (_property: BuyerUnit | BuyerProject) => {
		// This would typically open a rescheduling modal
		toast.info("Visit rescheduling feature coming soon");
	};

	const handleCancelVisit = async (_property: BuyerUnit | BuyerProject) => {
		// This would typically show a confirmation dialog and then cancel
		toast.info("Visit cancellation feature coming soon");
	};

	const handleRejectPropertySuggestion = async (
		property: BuyerSuggestion,
		reason?: string,
		source: BuyerActionSource = "unknown",
	) => {
		try {
			const propertyId = property.type === "unit" ? property.unitId : property.projectId;

			// Validate buyer group ID (handles race conditions)
			const currentBuyerGroupId = validateBuyerGroupId({
				method: "handleRejectPropertySuggestion",
				mutation: "REJECT_PREFERENCE_SUGGESTION_MUTATION",
				propertyId,
			});
			if (!currentBuyerGroupId) return;

			const result = await rejectPropertySuggestion({
				variables: {
					params: {
						rejectPrefSuggestions: {
							groupId: currentBuyerGroupId,
							propertyId,
							reason: reason || "User rejected from dashboard",
						},
					},
				},
			});
			if (result.data?.preferences?.rejectPrefSuggestions) {
				// Track rejection with source context
				trackEvent({
					google: {
						event: "reject_suggestion",
						options: {
							[property.type === "unit" ? "unit_id" : "project_id"]: propertyId,
							reason,
							source,
						},
					},
				});

				toast.success("Property rejected successfully");
			} else {
				toast.error("Failed to reject property - please try again");
			}
		} catch (error) {
			logger.error("Error rejecting property:", error);
			toast.error("Failed to reject property - please try again");
		}
	};
	const handleRejectProperty = async (
		property: BuyerUnit | BuyerProject,
		reason?: string,
		notes?: string,
		source: BuyerActionSource = "unknown",
	) => {
		try {
			const isUnit = "unit" in property && property.unit != null;
			const propertyId = isUnit ? property.unit?.id : (property as BuyerProject).project?.id;
			const propertyType = isUnit ? "BuyerUnit" : "BuyerProject";
			if (!propertyId) {
				toast.error("Property ID not found");
				return;
			}

			// Validate buyer group ID (handles race conditions)
			const currentBuyerGroupId = validateBuyerGroupId({
				method: "handleRejectProperty",
				mutation: "BUYER_REJECT_PROPERTY_MUTATION",
				propertyId,
				additionalContext: { propertyType },
			});
			if (!currentBuyerGroupId) return;

			const result = await rejectProperty({
				variables: {
					params: {
						rejectProperty: {
							action: "Reject",
							currGrpId: currentBuyerGroupId,
							propertyId: propertyId,
							propertyType: propertyType,
							reason: reason || "User rejected",
							notes: notes || "Rejected from dashboard",
						},
					},
				},
			});

			// Check if the mutation was successful
			if (result.data?.buyer?.rejectProperty) {
				// Track rejection with source context
				trackEvent({
					google: {
						event: isUnit ? "reject_unit" : "reject_project",
						options: {
							[isUnit ? "unit_id" : "project_id"]: propertyId,
							reason,
							source,
						},
					},
				});

				toast.success("Property rejected successfully");
			} else {
				toast.error("Failed to reject property - please try again");
			}
		} catch (error) {
			logger.error("Error rejecting property:", error);
			toast.error("Failed to reject property - please try again");
		}
	};

	const handleAcceptPropertyById = async ({
		propertyId,
		propertyType,
		source = "unknown",
	}: {
		propertyId: string;
		propertyType: "BuyerUnit" | "BuyerProject";
		source?: BuyerActionSource;
	}) => {
		try {
			if (!propertyId) {
				toast.error("Property ID not found");
				return;
			}

			// Validate buyer group ID (handles race conditions)
			const currentBuyerGroupId = validateBuyerGroupId({
				method: "handleAcceptPropertyById",
				mutation: "BUYER_REJECT_PROPERTY_MUTATION",
				propertyId,
				additionalContext: { propertyType },
			});
			if (!currentBuyerGroupId) return;

			const result = await rejectProperty({
				variables: {
					params: {
						rejectProperty: {
							action: "Revive",
							currGrpId: currentBuyerGroupId,
							propertyId: propertyId,
							propertyType: propertyType,
							reason: "User revived",
							notes: "Accepted from dashboard",
						},
					},
				},
			});

			// Check if the mutation was successful
			if (result.data?.buyer?.rejectProperty) {
				// Track accept/revive with source context
				const isUnit = propertyType === "BuyerUnit";
				trackEvent({
					google: {
						event: isUnit ? "accept_unit" : "accept_project",
						options: {
							[isUnit ? "unit_id" : "project_id"]: propertyId,
							source,
						},
					},
				});

				toast.success("Property accepted successfully");
			} else {
				toast.error("Failed to accept property - please try again");
			}
		} catch (error) {
			logger.error("Error accepting property:", error);
			toast.error("Failed to accept property - please try again");
		}
	};

	// New simplified rejection method for dashboard use
	const handleRejectPropertyById = async ({
		propertyId,
		propertyType,
		reason,
		notes,
		source = "unknown",
	}: {
		propertyId: string;
		propertyType: "BuyerUnit" | "BuyerProject";
		reason?: string;
		notes?: string;
		source?: BuyerActionSource;
	}) => {
		try {
			if (!propertyId) {
				toast.error("Property ID not found");
				return;
			}

			// Validate buyer group ID (handles race conditions)
			const currentBuyerGroupId = validateBuyerGroupId({
				method: "handleRejectPropertyById",
				mutation: "BUYER_REJECT_PROPERTY_MUTATION",
				propertyId,
				additionalContext: { propertyType },
			});
			if (!currentBuyerGroupId) return;

			const result = await rejectProperty({
				variables: {
					params: {
						rejectProperty: {
							action: "Reject",
							currGrpId: currentBuyerGroupId,
							propertyId: propertyId,
							propertyType: propertyType,
							reason: reason || "User rejected",
							notes: notes || "Rejected from dashboard",
						},
					},
				},
			});

			// Check if the mutation was successful
			if (result.data?.buyer?.rejectProperty) {
				// Track rejection with source context
				const isUnit = propertyType === "BuyerUnit";
				trackEvent({
					google: {
						event: isUnit ? "reject_unit_by_id" : "reject_project_by_id",
						options: {
							[isUnit ? "unit_id" : "project_id"]: propertyId,
							reason,
							source,
						},
					},
				});

				toast.success("Property rejected successfully");
			} else {
				toast.error("Failed to reject property - please try again");
			}
		} catch (error) {
			logger.error("Error rejecting property:", error);
			toast.error("Failed to reject property - please try again");
		}
	};

	const handleGiveOffer = async (
		property: BuyerUnit | BuyerProject,
		price?: string,
		source: BuyerActionSource = "unknown",
	) => {
		try {
			const isUnit = "unit" in property && property.unit != null;
			const unitId = isUnit ? property.unit?.id : undefined;

			if (!unitId) {
				toast.error("Unit ID not found - offers only available for units");
				return;
			}

			if (!price) {
				toast.error("Please provide an offer price");
				return;
			}

			// Validate buyer group ID (handles race conditions)
			const currentBuyerGroupId = validateBuyerGroupId({
				method: "handleGiveOffer",
				mutation: "BUYER_ACTIONS_MUTATION",
				propertyId: unitId,
			});
			if (!currentBuyerGroupId) return;

			const result = await submitBuyerAction({
				variables: {
					params: {
						buyerActions: {
							buyerId: currentBuyerGroupId,
							unitId: unitId,
							interested: true,
							price: price,
							data: {
								status: "interested",
							},
						},
					},
				},
			});

			if (result.data?.buyer?.buyerActions) {
				// Track offer submission with source context
				trackEvent({
					google: {
						event: "give_offer",
						options: {
							unit_id: unitId,
							offer_price: price,
							source,
						},
					},
					facebook: {
						event: "SubmitApplication",
						options: {
							content_id: unitId,
							value: price,
							currency: "INR",
							source,
						},
					},
				});

				toast.success("Offer submitted successfully");
			} else {
				toast.error("Failed to submit offer");
			}
		} catch (error) {
			logger.error("Error submitting offer:", error);
			toast.error("Failed to submit offer");
		}
	};

	const handleReviseOffer = async (
		property: BuyerUnit | BuyerProject,
		newPrice?: string,
		source: BuyerActionSource = "unknown",
	) => {
		try {
			const isUnit = "unit" in property && property.unit != null;
			const unitId = isUnit ? property.unit?.id : undefined;

			if (!unitId) {
				toast.error("Unit ID not found - offers only available for units");
				return;
			}

			if (!newPrice) {
				toast.error("Please provide a revised offer price");
				return;
			}

			// Validate buyer group ID (handles race conditions)
			const currentBuyerGroupId = validateBuyerGroupId({
				method: "handleReviseOffer",
				mutation: "BUYER_ACTIONS_MUTATION",
				propertyId: unitId,
			});
			if (!currentBuyerGroupId) return;

			const result = await submitBuyerAction({
				variables: {
					params: {
						buyerActions: {
							buyerId: currentBuyerGroupId,
							unitId: unitId,
							interested: true,
							price: newPrice,
							data: {
								status: "revised",
							},
						},
					},
				},
			});

			if (result.data?.buyer?.buyerActions) {
				// Track offer revision with source context
				trackEvent({
					google: {
						event: "revise_offer",
						options: {
							unit_id: unitId,
							offer_price: newPrice,
							source,
						},
					},
					facebook: {
						event: "SubmitApplication",
						options: {
							content_id: unitId,
							value: newPrice,
							currency: "INR",
							action_type: "revision",
							source,
						},
					},
				});

				toast.success("Offer revised successfully");
			} else {
				toast.error("Failed to revise offer");
			}
		} catch (error) {
			logger.error("Error revising offer:", error);
			toast.error("Failed to revise offer");
		}
	};

	return {
		handleShortlist,
		handleRemoveShortlist,
		handleScheduleVisit,
		handleReschedule,
		handleCancelVisit,
		handleRejectPropertySuggestion,
		handleRejectProperty,
		handleRejectPropertyById,
		handleAcceptPropertyById,
		handleGiveOffer,
		handleReviseOffer,
		// Loading states
		isShortlistLoading: shortlistLoading || removeShortlistLoading,
	};
}
